/**
 * 通用类型定义
 */

// API响应类型
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

// 分页响应类型
export interface PaginatedResponse<T = any> extends ApiResponse<T[]> {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// 工人类型枚举
export enum WorkerType {
  MALE = 'male',
  FEMALE = 'female',
  TECHNICIAN = 'technician',
  MANAGER = 'manager'
}

// 工时单位枚举
export enum TimeUnit {
  HALF = 'half',
  FULL = 'full'
}

// 项目状态枚举
export enum ProjectStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  PAUSED = 'paused'
}

// 预算模式枚举
export enum BudgetMode {
  AUTO = 'auto',     // 自动计算（总预算 = 细分之和）
  MANUAL = 'manual'  // 手动设置（允许总预算与细分不一致）
}

// 预算成分接口
export interface BudgetBreakdown {
  labor: number;        // 人工预算
  equipment: number;    // 机械预算
  material: number;     // 材料预算
  subcontract: number;  // 分包预算
  coordination: number; // 协调预算
  contingency: number;  // 应急储备
}

// 收入成分接口
export interface RevenueBreakdown {
  contract: number;
  changeOrder: number;
  claim: number;
  settlement: number;
  other: number;
}

// 预算调整类型枚举
export enum BudgetAdjustmentType {
  INITIAL = 'initial',       // 初始设置
  INCREASE = 'increase',     // 增加预算
  DECREASE = 'decrease',     // 减少预算
  REALLOCATION = 'reallocation', // 重新分配
  CORRECTION = 'correction'  // 修正错误
}

// 预算变化明细接口
export interface BudgetChanges {
  labor: number;
  equipment: number;
  material: number;
  coordination: number;
  contingency: number;
  subcontract: number;
  total: number;
}

// 预算调整记录接口
export interface BudgetAdjustment {
  _id: string;
  projectId: string;
  adjustmentType: BudgetAdjustmentType;
  reason: string;
  description?: string;
  previousBreakdown: BudgetBreakdown;
  previousTotal: number;
  newBreakdown: BudgetBreakdown;
  newTotal: number;
  changes: BudgetChanges;
  adjustedBy: {
    _id: string;
    username: string;
    realName?: string;
  };
  adjustedAt: string;
  approvedBy?: {
    _id: string;
    username: string;
    realName?: string;
  };
  approvedAt?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approvalComment?: string;
  createdAt: string;
  updatedAt: string;
}

// 预算详情接口
export interface BudgetDetail {
  projectId: string;
  projectName: string;
  budget: number;
  budgetBreakdown: BudgetBreakdown;
  calculatedTotal: number;
  difference: number;
  revenueTotal?: number;
  revenueBreakdown?: RevenueBreakdown;
  calculatedRevenueTotal?: number;
  revenueDifference?: number;
  budgetVersion?: number;
  budgetVersionCount?: number;
  latestVersionReason?: string;
  budgetMode: BudgetMode;
  budgetLocked: boolean;
  budgetLockedAt?: string;
  budgetLockedBy?: {
    _id: string;
    username: string;
    realName?: string;
  };
}

// 预算调整统计接口
export interface BudgetAdjustmentStats {
  byType: Array<{
    _id: BudgetAdjustmentType;
    count: number;
    totalChange: number;
  }>;
  totalAdjustments: number;
  netChange: number;
  firstAdjustment?: string;
  lastAdjustment?: string;
  currentVersion?: number;
  versionCount?: number;
  revenueTotal?: number;
}

// 分包成本状态枚举
export enum SubcontractCostStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

// 分包成本口径枚举
export enum SubcontractCostCaliber {
  RESPONSIBILITY = 'responsibility',
  SETTLEMENT = 'settlement',
  PAYMENT = 'payment'
}

// 分包结算状态枚举
export enum SubcontractSettlementStatus {
  UNSETTLED = 'unsettled',
  PARTIAL = 'partial',
  FULL = 'full'
}

export interface SubcontractorOption {
  _id: string;
  name: string;
  shortName?: string;
  contactPerson?: string;
  phone?: string;
  status?: 'active' | 'inactive' | 'blacklisted';
}

export interface SubcontractPayNode {
  nodeName: string;
  plannedAmount: number;
  paidAmount: number;
  plannedDate?: string;
  paidDate?: string;
  note?: string;
}

export interface SubcontractCost {
  _id: string;
  projectId: string;
  projectName?: string;
  subcontractorId?: string | SubcontractorOption;
  contractNo?: string;
  contractName: string;
  contractSignDate?: string;
  contractOwner?: string;
  contractSigner?: string;
  counterpartyName?: string;
  counterpartySigner?: string;
  costDate: string;
  contractAmount: number;
  settledAmount: number;
  paidAmount: number;
  unpaidAmount: number;
  paymentProgress: number;
  costCaliber: SubcontractCostCaliber;
  settlementStatus: SubcontractSettlementStatus;
  status: SubcontractCostStatus;
  payNodes: SubcontractPayNode[];
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubcontractCostData {
  projectId: string;
  subcontractorId?: string;
  contractNo?: string;
  contractName: string;
  contractSignDate?: string;
  contractOwner?: string;
  contractSigner?: string;
  counterpartyName?: string;
  counterpartySigner?: string;
  costDate?: string;
  contractAmount: number;
  settledAmount?: number;
  paidAmount?: number;
  status?: SubcontractCostStatus;
  costCaliber?: SubcontractCostCaliber;
  payNodes?: SubcontractPayNode[];
  description?: string;
}

export interface UpdateSubcontractCostData extends Partial<CreateSubcontractCostData> {}

// 协调费用类型枚举
export enum CoordinationCostType {
  MANAGEMENT = 'management',     // 管理费
  COORDINATION = 'coordination', // 协调费
  MISC = 'misc',                 // 杂费
  EMERGENCY = 'emergency'        // 应急费用
}

// 协调费用记录接口
export interface CoordinationCost {
  _id: string;
  projectId: string;
  costDate: string;
  costType: CoordinationCostType;
  amount: number;
  description?: string;
  handler?: string;
  createdAt: string;
  updatedAt: string;
}

// 创建协调费用的数据类型
export interface CreateCoordinationCostData {
  projectId: string;
  costDate?: string;
  costType: CoordinationCostType;
  amount: number;
  description?: string;
  handler?: string;
}

// 更新协调费用的数据类型
export interface UpdateCoordinationCostData {
  costDate?: string;
  costType?: CoordinationCostType;
  amount?: number;
  description?: string;
  handler?: string;
}

// 工时记录接口
export interface WorkTime {
  _id: string;
  projectId: string;
  project?: { name: string; _id: string };
  workDate: string;
  workerType: WorkerType;
  timeUnit: TimeUnit;
  workContent: string;
  price: number;
  maleWorkerCount: number;
  femaleWorkerCount: number;
  totalWorkerCount: number;
  constructionType?: '园林' | '土建' | '外墙' | '其它' | null;
  createdAt: string;
  updatedAt: string;
}

// 创建工时记录的数据类型
export interface CreateWorkTimeData {
  projectId: string;
  workDate?: string;
  workerType: WorkerType;
  timeUnit: TimeUnit;
  workContent: string;
  maleWorkerCount?: number;
  femaleWorkerCount?: number;
}

// 更新工时记录的数据类型
export interface UpdateWorkTimeData {
  projectId?: string;
  workDate?: string;
  workerType?: WorkerType;
  workUnits?: number;
  workContent?: string;
  maleWorkerCount?: number;
  femaleWorkerCount?: number;
}

// 项目接口
export interface Project {
  _id: string;
  name: string;
  projectCode?: string;
  description?: string;
  startDate: string;
  endDate?: string;
  status: ProjectStatus;
  budget?: number;
  budgetBreakdown?: BudgetBreakdown;
  revenueTotal?: number;
  revenueBreakdown?: RevenueBreakdown;
  budgetVersion?: number;
  budgetVersions?: Array<{
    version: number;
    budget: number;
    budgetBreakdown: BudgetBreakdown;
    revenueTotal: number;
    revenueBreakdown: RevenueBreakdown;
    reason?: string;
    createdAt: string;
    createdBy?: string;
  }>;
  budgetMode?: BudgetMode;
  budgetLocked?: boolean;
  budgetLockedAt?: string;
  budgetLockedBy?: {
    _id: string;
    username: string;
    realName?: string;
  };
  progress?: number;
  actualCost?: number;
  createdAt: string;
  updatedAt: string;
}

// 工时查询参数
export interface WorkTimeQueryParams {
  projectId?: string;
  startDate?: string;
  endDate?: string;
  workerType?: WorkerType;
  page?: number;
  limit?: number;
}

// 工时统计数据
export interface WorkTimeSummary {
  totalRecords: number;
  totalCost: number;
  totalDays: number;
  totalHalfDays: number;
  totalWorkUnits?: number;  // 总工时数量（可选，用于前端显示）
  maleWorkerCost: number;
  femaleWorkerCost: number;
}

// 设备使用记录接口
export interface EquipmentUsage {
  _id: string;
  projectId: string;
  equipmentName: string;
  usageDate: string;
  usageHours: number;
  cost?: number;
  remarks?: string;
  /** 施工类型：园林 / 土建 / 外墙 / 其它（与材料模块枚举一致） */
  constructionType?: '园林' | '土建' | '外墙' | '其它' | null;
  /** 经手人姓名 */
  handledByName?: string;
  /** 设备提供方 ID */
  equipmentProviderId?: string;
  /** 设备提供方名称 */
  equipmentProviderName?: string;
  createdAt: string;
  updatedAt: string;
}

// 创建设备使用记录的数据类型
export interface CreateEquipmentUsageData {
  projectId: string;
  equipmentName: string;
  usageDate?: string;
  usageHours: number;
  cost?: number;
  remarks?: string;
  constructionType?: '园林' | '土建' | '外墙' | '其它' | null;
  handledByName?: string;
  equipmentProviderId?: string;
  equipmentProviderName?: string;
}

// 更新设备使用记录的数据类型
export interface UpdateEquipmentUsageData {
  projectId?: string;
  equipmentName?: string;
  usageDate?: string;
  usageHours?: number;
  cost?: number;
  remarks?: string;
  constructionType?: '园林' | '土建' | '外墙' | '其它' | null;
  handledByName?: string;
  equipmentProviderId?: string;
  equipmentProviderName?: string;
}

// 设备使用查询参数
export interface EquipmentUsageQueryParams {
  projectId?: string;
  equipmentName?: string;
  startDate?: string;
  endDate?: string;
  constructionType?: string;
  handledByName?: string;
  equipmentProviderName?: string;
  page?: number;
  limit?: number;
}

// 设备使用统计数据
export interface EquipmentUsageSummary {
  totalRecords: number;
  totalHours: number;
  totalCost: number;
  uniqueEquipment: number;
  averageCostPerHour: number;
}

// 材料使用记录接口
export interface MaterialUsage {
  _id: string;
  projectId: string;
  materialName: string;
  usageDate: string;
  quantity: number;
  unit: string;
  unitPrice?: number;
  totalCost?: number;
  createdAt: string;
  updatedAt: string;
}

// 创建材料使用记录的数据类型
export interface CreateMaterialUsageData {
  projectId: string;
  materialName: string;
  usageDate?: string;
  quantity: number;
  unit: string;
  unitPrice?: number;
  constructionType?: '园林' | '土建' | '外墙' | '其它' | null;
}

// 更新材料使用记录的数据类型
export interface UpdateMaterialUsageData {
  projectId?: string;
  materialName?: string;
  usageDate?: string;
  quantity?: number;
  unit?: string;
  unitPrice?: number;
  constructionType?: '园林' | '土建' | '外墙' | '其它' | null;
}

// 材料使用查询参数
export interface MaterialUsageQueryParams {
  projectId?: string;
  materialName?: string;
  startDate?: string;
  endDate?: string;
  constructionType?: string;
  page?: number;
  limit?: number;
}

// 材料使用统计数据
export interface MaterialUsageSummary {
  totalRecords: number;
  totalQuantity: number;
  totalCost: number;
  uniqueMaterials: number;
  averageCostPerUnit: number;
}

// 材料解析草稿接口
export interface MaterialParseDraft {
  _id: string;
  userId: string;
  draftName?: string;
  projectId?: string;
  projectName?: string;
  materials: Array<{
    name: string;
    specification?: string;
    quantity: number;
    unit: string;
    unitPrice?: number;
    totalPrice?: number;
    remark?: string;
    submitStatus?: 'pending' | 'submitting' | 'success' | 'failed';
    submitError?: string;
    submitTime?: string;
    usageRecordId?: string;
    priceConfigId?: string;
    presetDataId?: string;
    operations?: MaterialOperations;
  }>;
  originalText?: string;
  status: 'draft' | 'partial_success' | 'all_success' | 'all_failed' | 'submitted';
  submissionSummary?: {
    totalCount: number;
    successCount: number;
    failedCount: number;
    lastSubmitTime?: string;
  };
  materialCount?: number;
  totalAmount?: number;
  createdAt: string;
  updatedAt: string;
}

// 创建材料解析草稿的数据类型
export interface CreateMaterialParseDraftData {
  draftName?: string;
  projectId?: string;
  projectName?: string;
  materials: Array<{
    name: string;
    specification?: string;
    quantity: number;
    unit: string;
    unitPrice?: number;
    totalPrice?: number;
    remark?: string;
  }>;
  originalText?: string;
}

// 更新材料解析草稿的数据类型
export interface UpdateMaterialParseDraftData {
  draftName?: string;
  projectId?: string;
  projectName?: string;
  materials?: Array<{
    name: string;
    specification?: string;
    quantity: number;
    unit: string;
    unitPrice?: number;
    totalPrice?: number;
    remark?: string;
  }>;
  originalText?: string;
}

// 材料解析草稿查询参数
export interface MaterialParseDraftQueryParams {
  status?: 'draft' | 'submitted';
  projectId?: string;
  keyword?: string;
  page?: number;
  limit?: number;
}

// 材料解析草稿统计
export interface MaterialParseDraftStats {
  totalDrafts: number;
  draftCount: number;
  submittedCount: number;
}

// 价格配置接口
export interface PriceConfig {
  _id: string;
  workerType: WorkerType;
  timeUnit: TimeUnit;
  price: number;
  effectiveDate: string;
  createdAt: string;
}

// 创建价格配置的数据类型
export interface CreatePriceConfigData {
  workerType: WorkerType;
  timeUnit: TimeUnit;
  price: number;
  effectiveDate?: string;
}

// 更新价格配置的数据类型
export interface UpdatePriceConfigData {
  workerType?: WorkerType;
  timeUnit?: TimeUnit;
  price?: number;
  effectiveDate?: string;
}

// 当前价格配置
export interface CurrentPrices {
  male: {
    half: number | null;
    full: number | null;
  };
  female: {
    half: number | null;
    full: number | null;
  };
}

// 价格配置查询参数
export interface PriceConfigQueryParams {
  workerType?: WorkerType;
  timeUnit?: TimeUnit;
  page?: number;
  limit?: number;
}

// 表单验证规则类型
export interface FormRule {
  required?: boolean;
  message?: string;
  validator?: (rule: any, value: any) => Promise<void>;
}

// 统计报表相关类型定义

// 项目综合统计数据
export interface ProjectComprehensiveStats {
  project: {
    id: string;
    name: string;
    description?: string;
    status: string;
    budget: number;
    manualProgress: number;
    startDate?: string;
    endDate?: string;
    updatedAt?: string;
  };
  period: {
    startDate: string;
    endDate: string;
  };
  workTime: {
    totalCost: number;
    maleWorkerCost: number;
    femaleWorkerCost: number;
    totalDays: number;
    totalHalfDays: number;
    recordCount: number;
  };
  equipment: {
    totalCost: number;
    totalHours: number;
    equipmentCount: number;
    recordCount: number;
  };
  material: {
    totalCost: number;
    totalQuantity: number;
    materialCount: number;
    recordCount: number;
  };
  summary: {
    totalProjectCost: number;
    budgetUsageRate: number | null;
    costBreakdown: {
      workTimeCost: number;
      equipmentCost: number;
      materialCost: number;
      subcontractCost: number;
      coordinationCost: number;
    };
    costPercentage: {
      workTimePercentage: number;
      equipmentPercentage: number;
      materialPercentage: number;
      subcontractPercentage: number;
      coordinationPercentage: number;
    };
    subcontractRule: {
      label: string;
      description: string;
    };
    scheduleStatus: {
      key: string;
      label: string;
      isOverdue: boolean;
      daysRemaining: number | null;
    };
    risk: {
      primaryRisk: string | null;
      riskTags: string[];
      reasons: string[];
      score: number;
    };
  };
  subcontract?: {
    totalCost: number;
    recordCount: number;
  };
  coordination?: {
    totalCost: number;
    byType?: {
      management: number;
      coordination: number;
      misc: number;
      emergency: number;
    };
    recordCount: number;
  };
};

// 多项目对比数据
export interface ProjectComparisonData {
  period: {
    startDate: string;
    endDate: string;
  };
  projects: Array<{
    projectId: string;
    projectName: string;
    totalCost: number;
    workTimeCost: number;
    equipmentCost: number;
    materialCost: number;
    totalRecords: number;
  }>;
  summary: {
    totalCostAllProjects: number;
    averageCostPerProject: number;
    highestCostProject: string;
    lowestCostProject: string;
  };
}

// 时间段分析数据
export interface TimeRangeAnalysisData {
  period: {
    startDate: string;
    endDate: string;
    durationDays: number;
  };
  workTime: {
    totalCost: number;
    totalRecords: number;
    averageCostPerDay: number;
    maleWorkerCost: number;
    femaleWorkerCost: number;
  };
  equipment: {
    totalCost: number;
    totalHours: number;
    totalRecords: number;
    averageHoursPerDay: number;
    uniqueEquipment: number;
  };
  material: {
    totalCost: number;
    totalQuantity: number;
    totalRecords: number;
    uniqueMaterials: number;
  };
  dailyTrends: Array<{
    date: string;
    workTimeCost: number;
    equipmentCost: number;
    materialCost: number;
    totalCost: number;
  }>;
}

// 成本汇总统计数据
export interface CostSummaryData {
  totalCost: number;
  workTimeCost: number;
  equipmentCost: number;
  materialCost: number;
  projectCount: number;
  recordCount: {
    workTime: number;
    equipment: number;
    material: number;
    total: number;
  };
  averages: {
    costPerProject: number;
    costPerRecord: number;
    workTimeCostPerRecord: number;
    equipmentCostPerRecord: number;
    materialCostPerRecord: number;
  };
}

// 报表查询参数
export interface ReportQueryParams {
  projectId?: string;
  projectIds?: string[];
  startDate?: string;
  endDate?: string;
}

// 多项目对比请求数据
export interface ProjectComparisonRequest {
  projectIds: string[];
  startDate?: string;
  endDate?: string;
}

// ==================== 库存和调拨相关类型 ====================

// 调拨类型枚举
export enum TransferType {
  PROJECT_TO_PROJECT = 'project_to_project',
  PROJECT_TO_INVENTORY = 'project_to_inventory',
  INVENTORY_TO_PROJECT = 'inventory_to_project'
}

// 调拨状态枚举
export enum TransferStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

// 库存流水类型枚举
export enum TransactionType {
  IN = 'in',
  OUT = 'out',
  ADJUST = 'adjust',
  MANUAL = 'manual'
}

// 材料库存接口
export interface MaterialInventory {
  _id: string;
  materialName: string;
  unit: string;
  quantity: number;
  totalCost: number;
  averageUnitPrice: number;
  minQuantity?: number;
  maxQuantity?: number;
  location?: string;
  images?: string[];
  remark?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// 创建库存记录的数据类型
export interface CreateInventoryData {
  materialName: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  location?: string;
  minQuantity?: number;
  maxQuantity?: number;
  images?: string[];
  remark?: string;
}

// 更新库存信息的数据类型
export interface UpdateInventoryData {
  location?: string;
  minQuantity?: number;
  maxQuantity?: number;
  remark?: string;
  images?: string[];
}

// 材料调拨记录接口
export interface MaterialTransfer {
  _id: string;
  transferNo: string;
  transferType: TransferType;
  materialName: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  totalCost: number;
  sourceType: 'project' | 'inventory';
  sourceProjectId?: string;
  sourceProjectName?: string;
  targetType: 'project' | 'inventory';
  targetProjectId?: string;
  targetProjectName?: string;
  status: TransferStatus;
  approvalRequired: boolean;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  originalMaterialUsageId?: string;
  remark?: string;
  transferDate: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// 创建调拨申请的数据类型
export interface CreateTransferData {
  transferType: TransferType;
  sourceMaterialUsageId?: string;
  sourceProjectId?: string;
  targetProjectId?: string;
  materialName?: string;
  unit?: string;
  quantity: number;
  images?: string[];
  remark?: string;
}

// 库存流水记录接口
export interface InventoryTransaction {
  _id: string;
  materialName: string;
  unit: string;
  transactionType: TransactionType;
  quantity: number;
  unitPrice: number;
  totalCost: number;
  relatedTransferId?: string;
  relatedProjectId?: string;
  beforeQuantity: number;
  afterQuantity: number;
  beforeCost: number;
  afterCost: number;
  remark?: string;
  transactionDate: string;
  createdBy: string;
  createdAt: string;
}

// 扩展的材料使用记录接口（包含调拨信息）
export interface MaterialUsageExtended extends MaterialUsage {
  transferredQuantity?: number;
  availableQuantity?: number;
  isFullyTransferred?: boolean;
  project?: {
    _id: string;
    name: string;
  };
}

// 库存统计数据
export interface InventoryStats {
  totalValue: number;
  totalItems: number;
  lowStockItems: number;
  overStockItems: number;
  zeroStockItems: number;
}

// 库存预警数据
export interface InventoryAlerts {
  lowStock: MaterialInventory[];
  overStock: MaterialInventory[];
  zeroStock: MaterialInventory[];
}

// 调拨统计数据
export interface TransferStats {
  totalTransfers: number;
  totalCost: number;
  byType: {
    [key in TransferType]: {
      count: number;
      cost: number;
    };
  };
  byStatus: {
    [key in TransferStatus]: number;
  };
}

// 库存查询参数
export interface InventoryQueryParams {
  materialName?: string;
  alertType?: 'low' | 'over' | 'zero';
  page?: number;
  limit?: number;
}

// 调拨查询参数
export interface TransferQueryParams {
  status?: TransferStatus;
  transferType?: TransferType;
  sourceProjectId?: string;
  targetProjectId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// 库存流水查询参数
export interface TransactionQueryParams {
  materialName?: string;
  unit?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// ==================== 工人管理相关类型 ====================
// 导出工人管理模块的所有类型
export * from './worker';
